/**
 * AI 分析 · 数据类型定义
 *
 * 调用链：
 *   1) createAITask({ aiTaskType: 100, input: { urls, edit_user }, userID })
 *      → 拿到 taskID
 *   2) pollAITaskResult({ taskID, userID })
 *      → 拿到 { taskStatus: 3, output: "<JSON string>" }
 *      注意：output 是 JSON 字符串本身，不是 URL！直接 JSON.parse 即可
 *   3) JSON.parse(output) as MingyangAiResult
 *
 * 字段命名严格对齐真实接口返回：
 *   - rules[].is_compliant   单条规则是否合规
 *   - res.all_compliant      整体是否合规
 *   - res.objects[]          识别到的物体/部件
 *   - res.total_rules / total_objects 统计计数
 *   - res.standard[]         标准（暂未明确语义，原样保留）
 *   - res.retrieval          命中的知识库检查项
 *   - res.warnings           注意事项（可选，可能为空或缺失）
 *   - res.usage / raw_output 元数据
 */

/** 一张待分析的团队照片（来自 PhotoPickerPage 返回） */
export interface MingyangAiPhoto {
  seq: number
  url: string
  small_url?: string
  media_type?: 0 | 1
}

/** 单条规则检查结果 */
export interface MingyangAiRule {
  /** 规则名称 */
  name: string
  /** 是否合规 —— 注意接口字段是 is_compliant 不是 compliant */
  is_compliant: boolean
  /** 判定理由 */
  reason: string
}

/** 识别到的物体 / 部件 */
export interface MingyangAiObject {
  name: string
  value: string
}

/** 命中的知识库检查项 */
export interface MingyangAiRetrieval {
  hit: boolean
  /** 检索方式：keyword / vector / hybrid 等 */
  method: string
  knowledge_id: number
  knowledge_inspection: string
}

/** LLM token 用量 */
export interface MingyangAiUsage {
  input_tokens: number
  output_tokens: number
}

/** 分析结果的 res 主体 */
export interface MingyangAiResultRes {
  /** 整体合规判定 */
  all_compliant: boolean
  rules: MingyangAiRule[]
  objects?: MingyangAiObject[]
  total_objects?: number
  total_rules?: number
  /** 标准列表，结构待后端明确，先用 any[] 兜底 */
  standard?: any[]
  retrieval?: MingyangAiRetrieval
  raw_output?: string
  usage?: MingyangAiUsage
  warnings?: string[]
}

/** JSON.parse(output) 得到的完整结果 */
export interface MingyangAiResult {
  res: MingyangAiResultRes
  /** 1 = 成功；其他值视为失败 */
  success: number
  reason: string
  show_reason: string
}

/** AI 任务类型常量（aiTaskType） */
export const MINGYANG_AI_TASK_TYPE = 100
